import React, { useState, useEffect, useCallback } from 'react';
import AntTable from 'common_components/AntTable';
import EditButtonRenderer from 'common_components/EditButtonRenderer';
import LinkRenderer from 'common_components/LinkRenderer';
import TableToolbar from 'common_components/AntTable/Toolbar';
import { push } from 'connected-react-router';
import { message } from 'antd';
import { CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons';
import { dateTimeTimestamptzFormatter } from 'common_components/AntTable/tableUtils';
import permission from 'permissions';
import { useSelector, useDispatch } from 'react-redux';
import { getCurrentUser } from 'reducers/user';
import { useErrorHandler } from 'common_components/ErrorContext';
import { Sorter } from 'utils/sorter';
import styled from 'styled-components';
import { selectTableStateById } from 'reducers/table';
import { useDeleteLocationMasterMutation } from 'reducers/masterData/locationMaster/api';

const TableComponent = (props) => {
  const { rowData, isLoading } = props;
  const [dataSource, setDataSource] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const [showDelete, setShowDelete] = useState(false);

  const dispatch = useDispatch();
  const { setErrorData } = useErrorHandler();
  const user = useSelector(getCurrentUser);
  const tableIdSelector = useSelector((state) =>
    selectTableStateById(state, 'location')
  );

  const [columns, setColumns] = useState(() => {
    let cols = [
      {
        title: '',
        dataIndex: 'edit',
        align: 'center',
        render: (text, record) => {
          return (
            <EditButtonRenderer data={record} onClick={handleEditClicked} />
          );
        },
        width: 50,
      },
      {
        title: 'Code',
        dataIndex: 'code',
        width: 150,
        align: 'center',
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'Name',
        dataIndex: 'name',
        width: 200,
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'Shortname',
        dataIndex: 'short_name',
        width: 150,
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'Cluster',
        dataIndex: 'cluster',
        align: 'center',
        render: (text, record) => {
          return (
            <LinkRenderer
              data={record}
              field='cluster'
              name='name'
              path='/app/master/clusters'
            />
          );
        },
        width: 200,
        movable: true,
      },
      {
        title: 'Has time windows',
        dataIndex: 'time_windows',
        width: 80,
        align: 'center',
        render: (text, record) => {
          if (text.length > 0) {
            return <CheckCircleTwoTone twoToneColor='#52c41a' />;
          } else {
            return <CloseCircleTwoTone twoToneColor='#eb2f96' />;
          }
        },
        sorter: {
          compare: Sorter.DEFAULT,
        },
      },
      {
        title: 'Location Type',
        dataIndex: 'location_type',
        width: 100,
        align: 'center',
        render: (text, record) => {
          return text.name;
        },
      },
      {
        title: 'Has Lat/Lng',
        dataIndex: 'lat',
        width: 80,
        align: 'center',
        render: (text, record) => {
          if (record && record.lat && record.lng) {
            return <CheckCircleTwoTone twoToneColor='#52c41a' />;
          } else {
            return <CloseCircleTwoTone twoToneColor='#eb2f96' />;
          }
        },
        sorter: {
          compare: Sorter.DEFAULT,
        },
      },
      {
        title: 'Geometry',
        dataIndex: 'geofence_type',
        width: 150,
        align: 'center',
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
      },
      {
        title: 'Radius',
        dataIndex: 'radius',
        width: 150,
        align: 'center',
        render: (text, record) => {
          return text || '-';
        },
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
      },
      {
        title: 'Updated time',
        dataIndex: 'updated_at',
        render: (text, record) => {
          return dateTimeTimestamptzFormatter({ value: text });
        },
        sorter: {
          compare: Sorter.DATE,
        },
        movable: true,
      },
      {
        title: 'Created time',
        dataIndex: 'created_at',
        render: (text, record) => {
          return dateTimeTimestamptzFormatter({ value: text });
        },
        sorter: {
          compare: Sorter.DATE,
        },
        movable: true,
      },
    ];

    // Add Show/Hide status from Redux store
    if (tableIdSelector) {
      cols = cols.map((c) => ({
        ...c,
        ...tableIdSelector.find((t) => t.dataIndex === c.dataIndex),
      }));
    }
    return cols;
  });

  const [
    deleteLocationMaster,
    {
      isSuccess: isSuccessDeleting,
      isUninitialized: isUninitDeleting,
      error: errorDeleting,
      isLoading: isDeleting,
    },
  ] = useDeleteLocationMasterMutation();

  useEffect(() => {
    setDataSource(rowData);
  }, [rowData]);

  useEffect(() => {
    if (errorDeleting) {
      setErrorData(errorDeleting);
    }
  }, [errorDeleting, setErrorData]);

  useEffect(() => {
    if (!isUninitDeleting && isSuccessDeleting) {
      message.success('Data was successfully deleted');
      clearSelectedRows();
    }
  }, [isSuccessDeleting, isUninitDeleting]);

  const onSearch = useCallback(
    (searchTerm) => {
      if (rowData && rowData.length > 0) {
        let searchCols = columns.filter((c) => c.searchable);
        if (searchCols.length > 0 && searchTerm) {
          let filterData = rowData.filter((d) =>
            searchCols.some(
              (s) =>
                d[s.dataIndex] &&
                String(d[s.dataIndex])
                  .toLowerCase()
                  .includes(searchTerm.trim().toLowerCase())
            )
          );
          setDataSource(filterData);
        } else {
          setDataSource(rowData);
        }
      }
      clearSelectedRows();
    },
    [columns, rowData]
  );

  const onCreate = () => {
    dispatch(push(`/app/master/location_masters/create`));
  };

  const handleEditClicked = (cell, readOnly) => {
    dispatch(push(`/app/master/location_masters/${cell.id}`));
  };

  const onDelete = () => {
    // TODO: Bulk delete
    for (let row of selectedRows) {
      deleteLocationMaster({ id: row.id });
    }

    clearSelectedRows();
  };

  const onReload = () => {
    props.refetchData();
    clearSelectedRows();
  };

  const clearSelectedRows = () => {
    setSelectedRows([]);
    setSelectedRowKeys([]);
    setShowDelete(false);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
      setSelectedRows(selectedRows);

      if (permission.checkPermission(user, 'location', ['d'])) {
        setShowDelete(selectedRows.length > 0 ? true : false);
      }
    },
  };

  return (
    <Container>
      <TableToolbar
        onCreate={onCreate}
        onDelete={onDelete}
        onReload={onReload}
        showDelete={showDelete}
        showReload={true}
        isLoading={isLoading}
        isDeleting={isDeleting}
        showAdd={permission.checkPermission(user, 'location', ['c'])}
        showSearch={true}
        columns={columns}
        setColumns={setColumns}
        onSearch={onSearch}
      />
      <AntTable
        rowKey='id'
        tableId={'location'}
        rowSelection={rowSelection}
        locationed
        showSorterTooltip={false}
        loading={isLoading || isDeleting}
        columns={columns}
        dataSource={dataSource}
        scroll={{ x: 'max-content' }}
        pagination={{ defaultPageSize: 20 }}
        // Below is for right click context menu
        showContextMenu={true}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        menuActions={[
          { label: 'Edit', action: (row) => handleEditClicked(row) },
          {
            label: 'Delete',
            action: (row) => deleteLocationMaster({ id: row.id }),
            delete: true,
          },
        ]}
      />
    </Container>
  );
};

const Container = styled.div`
  padding: 0 10px;
`;

export default TableComponent;
